// Org switcher dropdown — sits in the topbar next to the user menu.
// Lists every org the current user is a member of, with a check mark
// on the active one. Clicking another org calls POST /orgs/switch
// and reloads the page so every query refetches against the new org.
//
// Replaces the devtools-console snippet we used pre-Phase 4. When a
// user only has one org, the dropdown still renders but is non-
// interactive (shows the active org as a static badge).

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EyeOff } from 'lucide-react';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { toast } from './Toast.js';

interface OrgMembership {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  isActive: boolean;
  // CTSK-283 — per-org accent color (#RRGGBB, null = app default).
  // Drives the swatch next to each org row so orgs are visually
  // tellable-apart before switching.
  themeColor?: string | null;
  // 0.1.7 — orthogonal lifecycle flags. isHidden hides the org from
  // the default switcher list (operator opts in to see it via the
  // "Show hidden" toggle). archivedAt drives a read-only banner.
  isHidden: boolean;
  archivedAt: string | null;
  myMembership: {
    isOwner: boolean;
    joinedAt: string;
  };
}

export function OrgSwitcher({
  activeOrgSlug,
  onNavigate,
}: {
  activeOrgSlug?: string | null;
  // 0.1.7 — passed from AppShell so the "Manage orgs" footer link
  // navigates without a full page reload, AND so the dropdown can
  // route the operator to Settings → Organizations for richer
  // lifecycle actions (archive, delete) that don't belong in a
  // 240px menu.
  onNavigate?: (path: string) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Inline unhide. When the operator has toggled "Show hidden" and
  // sees an org chipped HIDDEN in the menu, a one-click unhide is
  // far more ergonomic than navigating to Settings → Organizations,
  // toggling Show hidden THERE, finding the row, clicking the eye
  // icon. Server-side cap check (orgs:hide OR per-org owner) still
  // applies — failed unhide surfaces via toast.fromError.
  const unhideMut = useMutation({
    mutationFn: (orgId: string) =>
      apiSingle<unknown>(`/orgs/${orgId}/visibility`, {
        method: 'PATCH',
        body: { isHidden: false },
      }),
    onSuccess: () => {
      toast.success('Org unhidden');
      qc.invalidateQueries({ queryKey: ['my-orgs'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to unhide org'),
  });

  // ?includeHidden=true when the operator has flipped the toggle.
  // When off, the API filters is_hidden orgs out of the list — the
  // switcher mirrors that default so hidden orgs disappear unless
  // the operator wants to see them.
  const orgsQ = useQuery({
    queryKey: ['my-orgs', { includeHidden: showHidden }],
    queryFn: () => apiList<OrgMembership>('/orgs', {
      query: { includeHidden: showHidden ? 'true' : undefined },
    }),
  });

  // Close on outside click. Cheap and good enough for a single dropdown;
  // no need for a portal or focus-trap library.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const orgs = orgsQ.data?.data ?? [];
  const activeOrg = orgs.find((o) => o.isActive) ?? orgs.find((o) => o.slug === activeOrgSlug);

  async function switchTo(orgId: string) {
    if (switching) return;
    setSwitching(true);
    try {
      await apiFetch('/orgs/switch', {
        method: 'POST',
        body: { orgId },
      });
      // Hard refresh so every TanStack Query refetches against the
      // new org. A softer alternative would be invalidateQueries() but
      // some pages cache derived state in component-local useState
      // (filters, pagination cursors) that should reset on org-change.
      window.location.reload();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to switch org', err);
      setSwitching(false);
      setOpen(false);
    }
  }

  if (orgs.length === 0) {
    // No memberships — should be rare; render a hint rather than a
    // broken-looking blank.
    return (
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        no org
      </span>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switching}
        aria-haspopup="menu"
        aria-expanded={open}
        title={activeOrg ? `Active org: ${activeOrg.name}` : 'Choose an org'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          background: 'var(--surface-sunken)',
          border: '1px solid var(--border-strong)',
          borderRadius: 6,
          fontSize: 13,
          color: 'var(--text-primary)',
          cursor: switching ? 'wait' : 'pointer',
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span style={{ opacity: 0.6 }}>org:</span>
        <span style={{ fontWeight: 600 }}>{activeOrg?.slug ?? '—'}</span>
        <span style={{ opacity: 0.5, fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 240,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: 6,
            zIndex: 100,
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              padding: '6px 10px',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--text-muted)',
            }}
          >
            Your organizations
          </div>
          {orgs.map((o) => {
            const isActive = o.isActive || o.slug === activeOrgSlug;
            const isArchived = !!o.archivedAt;
            // The org row is a clickable <div> (not <button>) so we
            // can nest the Unhide button inside it without a
            // button-in-button hierarchy. onClick on the wrapper
            // handles the switch; the Unhide button stops propagation.
            return (
              <div
                key={o.id}
                role="menuitem"
                onClick={() => { if (!isActive && !switching) switchTo(o.id); }}
                aria-disabled={isActive || switching}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  background: isActive ? 'var(--surface-sunken)' : 'transparent',
                  borderRadius: 4,
                  cursor: isActive ? 'default' : 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--surface-sunken)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: 12, display: 'inline-block', color: 'var(--accent-primary)' }}>
                  {isActive ? '✓' : ''}
                </span>
                {/* CTSK-283 — org theme swatch: that org's saved accent,
                    or the app default when unset. Tiny, aria-hidden
                    (decorative — the name is the accessible label). */}
                <span
                  aria-hidden
                  title={o.themeColor ? `Theme ${o.themeColor}` : 'Theme: app default'}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    flex: '0 0 auto',
                    background: o.themeColor ?? 'var(--accent-primary)',
                    border: '1px solid color-mix(in srgb, var(--text-primary) 25%, transparent)',
                    opacity: o.themeColor ? 1 : 0.45,
                  }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: isActive ? 600 : 400, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{o.name}</span>
                    {isArchived && (
                      <span title={`Archived ${new Date(o.archivedAt!).toLocaleString()}`} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'color-mix(in srgb, var(--warning-primary) 14%, var(--surface-raised))', color: 'var(--warning-primary)', fontWeight: 600 }}>
                        ARCHIVED
                      </span>
                    )}
                    {o.isHidden && (
                      <span title="Hidden — only visible with Show hidden" style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'var(--surface-sunken)', color: 'var(--text-muted)', fontWeight: 600 }}>
                        HIDDEN
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-muted)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {o.slug}
                    {o.myMembership.isOwner && (
                      <span style={{ marginLeft: 6, opacity: 0.7 }}>· owner</span>
                    )}
                  </div>
                </span>
                {/* Inline unhide. Only renders when the org IS hidden
                    (no point showing it on visible orgs). Stops
                    propagation so clicking it doesn't switch into
                    the org as a side effect. */}
                {o.isHidden && (
                  <button
                    type="button"
                    // EyeOff = the org is currently hidden (state-based).
                    // Tooltip says click-to-unhide so the action is
                    // explicit. Matches the Settings panel's icon.
                    title="Click to unhide (currently hidden)"
                    aria-label={`Unhide ${o.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      unhideMut.mutate(o.id);
                    }}
                    disabled={unhideMut.isPending}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      padding: 0,
                      background: 'transparent',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 4,
                      color: 'var(--text-secondary)',
                      cursor: unhideMut.isPending ? 'wait' : 'pointer',
                      flex: '0 0 auto',
                    }}
                  >
                    <EyeOff size={12} />
                  </button>
                )}
              </div>
            );
          })}
          {/* Show-hidden toggle. When on, /orgs returns is_hidden orgs too,
              and they appear in the menu with a HIDDEN chip. Useful for an
              operator who's parked orgs they don't want to see day-to-day
              but still needs to manage / switch into occasionally. */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderTop: '1px solid var(--border-subtle)',
              fontSize: 11,
              color: 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
            />
            Show hidden orgs
          </label>
          <div
            style={{
              padding: '6px 10px',
              fontSize: 11,
              color: 'var(--text-muted)',
            }}
          >
            {onNavigate ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onNavigate('/settings?section=organizations');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--text-muted)',
                  fontSize: 11,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                Manage orgs in <strong style={{ color: 'var(--accent-primary)' }}>Settings → Organizations</strong> →
              </button>
            ) : (
              <span>Manage orgs in <strong>Settings → Organizations</strong></span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
